import { useCallback, useMemo, useState } from 'react'
import { ListChecks, Wand2, FlaskConical, Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { useToast } from '@/providers/UiProviders'
import { ModListScreen } from './ModListScreen'
import {
  makeFetcher,
  addKeywordList,
  deleteKeywordList,
  createModRule,
  testModRule,
  MOD_STR as S,
  type KeywordListRow,
  type ModRuleRow,
} from './modContentApi'

/**
 * A6.28 — Kalit so'zlar ro'yxatlari. Til / toifa bo'yicha ro'yxatlar
 * (GET/POST/DELETE /admin/moderation/keyword-lists). Qo'shish/o'chirish REAL.
 */
export function KeywordListsScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const columns = useMemo<AdminColumn<KeywordListRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'name', header: S.listName, type: 'text' },
      { key: 'lang', header: S.lang, type: 'text' },
      { key: 'entries', header: S.entries, type: 'number' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'enabled', header: S.enabled, type: 'bool' },
      { key: 'updated_at', header: S.updatedAt, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<KeywordListRow>(
        '/admin/moderation/keyword-lists',
        'lists',
        (r) => `${r.name} ${r.lang} ${r.category}`,
      ),
    [reloadKey],
  )
  const add = useCallback(async () => {
    const name = (window.prompt(S.namePrompt) ?? '').trim()
    if (!name) return
    const lang = (window.prompt(S.langPrompt, 'uz') ?? 'uz').trim() || 'uz'
    const category = (window.prompt(S.category) ?? 'spam').trim() || 'spam'
    try {
      await addKeywordList({ name, lang, category })
      toast(S.listAdded, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }, [toast])
  const remove = useCallback(
    async (row: KeywordListRow) => {
      if (!window.confirm(S.confirmDelete)) return
      try {
        await deleteKeywordList(row.id)
        toast(S.listDeleted, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )
  const rowActions = useMemo<RowAction<KeywordListRow>[]>(
    () => [{ id: 'del', label: S.delete, danger: true, onSelect: (r) => void remove(r) }],
    [remove],
  )
  return (
    <ModListScreen<KeywordListRow>
      screenId="A6.28"
      title={S.keywords}
      description={S.keywordsDesc}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      icon={<ListChecks size={28} strokeWidth={1.5} />}
      rowActions={rowActions}
      actions={
        <button
          onClick={() => void add()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={14} />
          {S.add}
        </button>
      }
    />
  )
}

/**
 * A6.33 — Qoidalar konstruktori (if-then). Shart → amal qoidalar ro'yxati
 * (GET/POST /admin/moderation/rules). Qo'shish REAL.
 */
export function RuleBuilderScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const columns = useMemo<AdminColumn<ModRuleRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'name', header: S.ruleName, type: 'text' },
      { key: 'condition', header: S.condition, type: 'longtext' },
      { key: 'action', header: S.action, type: 'text' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'enabled', header: S.enabled, type: 'bool' },
      { key: 'hits', header: S.hits, type: 'number' },
      { key: 'updated_at', header: S.updatedAt, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<ModRuleRow>(
        '/admin/moderation/rules',
        'rules',
        (r) => `${r.name} ${r.condition} ${r.action}`,
      ),
    [reloadKey],
  )
  const add = useCallback(async () => {
    const name = (window.prompt(S.ruleName) ?? '').trim()
    const condition = (window.prompt(S.condition) ?? '').trim()
    const action = (window.prompt(S.action, 'flag') ?? '').trim()
    if (!name || !condition || !action) {
      if (name || condition || action) toast(S.needRule, { tone: 'warning' })
      return
    }
    const category = (window.prompt(S.category, 'spam') ?? 'spam').trim() || 'spam'
    try {
      await createModRule({ name, condition, action, category })
      toast(S.ruleAdded, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }, [toast])
  return (
    <ModListScreen<ModRuleRow>
      screenId="A6.33"
      title={S.rules}
      description={S.rulesDesc}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      icon={<Wand2 size={28} strokeWidth={1.5} />}
      actions={
        <button
          onClick={() => void add()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={14} />
          {S.addRule}
        </button>
      }
    />
  )
}

/**
 * A6.34 — Qoida testi. Shablon / regex ni namuna matnda sinash
 * (POST /admin/moderation/test) — real backend (autoModApi bilan bir endpoint).
 */
export function RuleTestScreen() {
  const { toast } = useToast()
  const [pattern, setPattern] = useState('')
  const [isRegex, setIsRegex] = useState(false)
  const [sample, setSample] = useState('')
  const [result, setResult] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  const run = useCallback(async () => {
    if (!pattern.trim() || !sample.trim()) return
    setBusy(true)
    setResult(null)
    try {
      const r = await testModRule(pattern.trim(), isRegex, sample)
      setResult(!!r.matched)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }, [pattern, isRegex, sample, toast])

  const fieldCls =
    'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary'

  return (
    <>
      <PageHeader title={S.ruleTest} screenId="A6.34" description={S.ruleTestDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-2xl space-y-2.5 rounded-lg border border-border-default bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={S.pattern}
              className={`${fieldCls} flex-1`}
            />
            <label className="inline-flex items-center gap-1.5 text-sm text-secondary">
              <input type="checkbox" checked={isRegex} onChange={(e) => setIsRegex(e.target.checked)} />
              {S.regex}
            </label>
          </div>
          <textarea
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            placeholder={S.sample}
            rows={3}
            className={`${fieldCls} w-full resize-y`}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={() => void run()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              <FlaskConical size={14} />
              {S.test}
            </button>
            {result !== null && (
              <span
                className={`text-sm font-medium ${result ? 'text-emerald-400' : 'text-tertiary'}`}
              >
                {result ? S.matched : S.noMatch}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
